// Error Tracking Service - Қателерді серверге жіберу
import axios from 'axios';
import { useAuthStore } from '@/stores/auth';

const API_URL = import.meta.env.VITE_API_URL || '/api';

class ErrorTracker {
  constructor() {
    this.initialized = false;
    this.errorQueue = [];
    this.isOnline = navigator.onLine;
    this.recentErrors = new Map(); // deduplication map
    
    // Зиянсыз браузер қателерін фильтрлеу — қолданушы құрылғысының шектеуінен туындайтын,
    // біздің кодқа байланысты емес қателер
    this.ignoredPatterns = [
      'ResizeObserver loop',
      'The play() request was interrupted',
      'The operation was aborted',
      'AbortError',
      'Script error.',
      "Can't find variable: EmptyRanges",
      'findLast is not a function',
      "evaluating 'media.currentTime'",
      // Graphics / WebGL — iPhone Safari, low-power mode, too many contexts
      'WebGL context',
      'Error creating WebGL',
      'WebGL: CONTEXT_LOST',
      'CONTEXT_LOST_WEBGL',
      // Network / extension noise
      'Failed to fetch',
      'Load failed',
      'NetworkError when attempting',
      'cancelled',
      // iOS Safari quirks
      'null is not an object (evaluating',
      'undefined is not an object (evaluating',
      // Browser extensions
      'extension://',
      'chrome-extension://'
    ];

    // Network status listener
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.flushQueue();
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  /**
   * Initialize error tracking
   */
  init() {
    if (this.initialized) return;

    // Global error handler
    window.onerror = (message, source, lineno, colno, error) => {
      this.trackError({
        errorType: 'CLIENT_ERROR',
        message: message || 'Unknown error',
        stack: error?.stack || `at ${source}:${lineno}:${colno}`,
        url: window.location.href
      });
      return false; // Don't prevent default error handling
    };

    // Unhandled promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      this.trackError({
        errorType: 'CLIENT_ERROR',
        errorCode: 'UNHANDLED_PROMISE',
        message: event.reason?.message || String(event.reason) || 'Unhandled promise rejection',
        stack: event.reason?.stack || null,
        url: window.location.href
      });
    });

    // Vue error handler (to be called from Vue app setup)
    this.vueErrorHandler = (err, vm, info) => {
      this.trackError({
        errorType: 'CLIENT_ERROR',
        errorCode: 'VUE_ERROR',
        message: err?.message || 'Vue error',
        stack: err?.stack || null,
        url: window.location.href,
        method: info
      });
    };

    this.initialized = true;
    console.log('🛡️ Error tracking initialized');
  }

  /**
   * Get current user info
   */
  getUserInfo() {
    try {
      const authStore = useAuthStore();
      if (authStore && authStore.user) {
        return {
          userId: authStore.user.id,
          userPhone: authStore.user.phone,
          userName: authStore.user.name
        };
      }
    } catch (e) {
      // Store might not be ready yet
    }
    return { userId: null, userPhone: null, userName: null };
  }

  /**
   * Track an error
   * @param {Object} errorData - Error data to track
   */
  async trackError(errorData) {
    try {
      const msg = errorData.message || '';
      
      // Зиянсыз қателерді фильтрлеу
      if (this.ignoredPatterns.some(p => msg.includes(p))) return;
      
      // Дедупликация — бір қатені 60 сек ішінде қайта жібермеу
      const key = `${errorData.errorCode || ''}:${msg.slice(0, 100)}`;
      const now = Date.now();
      if (this.recentErrors.has(key) && now - this.recentErrors.get(key) < 60000) return;
      this.recentErrors.set(key, now);
      // Ескі жазбаларды тазалау
      if (this.recentErrors.size > 50) {
        for (const [k, t] of this.recentErrors) {
          if (now - t > 60000) this.recentErrors.delete(k);
        }
      }

      const userInfo = this.getUserInfo();
      
      const payload = {
        ...userInfo,
        errorType: errorData.errorType || 'CLIENT_ERROR',
        errorCode: errorData.errorCode || null,
        message: this.truncate(errorData.message, 1000),
        stack: this.truncate(errorData.stack, 5000),
        url: errorData.url || window.location.href,
        method: errorData.method || null,
        requestBody: errorData.requestBody || null
      };

      if (!this.isOnline) {
        this.errorQueue.push(payload);
        console.warn('📴 Offline - Error queued for later');
        return;
      }

      await this.sendError(payload);
    } catch (e) {
      console.error('Failed to track error:', e);
    }
  }

  /**
   * Send error to server
   */
  async sendError(payload) {
    try {
      await axios.post(`${API_URL}/errors`, payload, {
        timeout: 5000 // 5 second timeout
      });
      console.log('📤 Error logged to server');
    } catch (e) {
      // Drop on transient infra failures so we don't:
      //  - spam the queue every 10s while the server is down
      //  - cause a flood when the server comes back and flushQueue() runs
      const status = e?.response?.status;
      const isTransient = !e?.response || (typeof status === 'number' && status >= 500);
      if (isTransient) {
        // Quietly drop — these are server/network issues, not user errors
        return;
      }
      // Other failures (4xx like rate-limit) — keep behavior, queue for later
      console.warn('Failed to send error to server, queuing...', e);
      this.errorQueue.push(payload);
      // Cap the queue so it can't grow without bound
      if (this.errorQueue.length > 50) this.errorQueue = this.errorQueue.slice(-50);
    }
  }

  /**
   * Flush queued errors
   */
  async flushQueue() {
    if (this.errorQueue.length === 0) return;
    
    console.log(`📤 Flushing ${this.errorQueue.length} queued errors`);
    
    const errors = [...this.errorQueue];
    this.errorQueue = [];

    for (const error of errors) {
      try {
        await this.sendError(error);
      } catch (e) {
        // Re-queue if failed
        this.errorQueue.push(error);
      }
    }
  }

  /**
   * Track API error specifically
   * @param {Error} error - Axios error object
   * @param {Object} config - Request config
   */
  trackApiError(error, config) {
    const response = error.response;
    
    this.trackError({
      errorType: 'API_ERROR',
      errorCode: response?.status?.toString() || 'NETWORK_ERROR',
      message: response?.data?.message || response?.data?.error || error.message,
      stack: error.stack,
      url: config?.url || 'Unknown',
      method: config?.method?.toUpperCase() || 'Unknown',
      requestBody: config?.data
    });
  }

  /**
   * Track auth error
   * @param {string} message - Error message
   */
  trackAuthError(message) {
    this.trackError({
      errorType: 'AUTH_ERROR',
      message: message,
      url: window.location.href
    });
  }

  /**
   * Track test error
   * @param {string} message - Error message
   * @param {Object} testInfo - Additional test info
   */
  trackTestError(message, testInfo = {}) {
    this.trackError({
      errorType: 'TEST_ERROR',
      message: message,
      url: window.location.href,
      requestBody: testInfo
    });
  }

  /**
   * Truncate string to specified length
   */
  truncate(str, maxLength) {
    if (!str) return str;
    if (str.length <= maxLength) return str;
    return str.substring(0, maxLength) + '...';
  }
}

// Singleton instance
const errorTracker = new ErrorTracker();

export default errorTracker;

// Export convenience functions
export const trackError = (errorData) => errorTracker.trackError(errorData);
export const trackApiError = (error, config) => errorTracker.trackApiError(error, config);
export const trackAuthError = (message) => errorTracker.trackAuthError(message);
export const trackTestError = (message, testInfo) => errorTracker.trackTestError(message, testInfo);
export const initErrorTracking = () => errorTracker.init();
export const getVueErrorHandler = () => errorTracker.vueErrorHandler;
